import React, { FC, ReactNode, useState } from 'react';
import cn from 'classnames';

export enum ButtonVariant {
  minimalistic,
  regular
}

export const ButtonColor = {
  gray: { default: 'bg-gallery' }
};

export const TextColor = {
  gray: {
    default: 'text-doveGray',
    hover: 'hover:text-vermilion',
    active: 'active:text-vermilion',
    disabled: 'disabled:text-silver'
  }
};

type IconPropsType = {
  width?: number;
  height?: number;
  fill: string;
};

export const ButtonIcon = {
  default: {
    fill: '#80A2B1'
  },
  hover: {
    fill: '#FF4200'
  },
  disabled: {
    fill: '#D8D8D8'
  },
  active: {
    fill: '#40748B'
  }
};

export enum ButtonSizeList {
  sm = 'h-[32px]',
  base = 'h-[40px]'
}

type ColorState = keyof typeof ButtonColor;
interface Props extends React.HTMLProps<HTMLButtonElement> {
  onClick?: (event: React.FormEvent<HTMLButtonElement>) => void;
  buttonLabel?: ReactNode;
  className?: string;
  disabled?: boolean;
  variant?: ButtonVariant;
  backGroundColor?: typeof ButtonColor[ColorState];
  textColor?: typeof TextColor[ColorState];
  type?: 'submit' | 'reset' | 'button' | undefined;
  icon?: {
    size?: { width: number; height: number };
    alt: string;
    component: FC<IconPropsType>;
  };
  buttonSize?: ButtonSizeList;
  iconClass?: string;
}

const Button: FC<Props> = ({
  buttonLabel,
  onClick,
  className = '',
  disabled = false,
  variant = ButtonVariant.regular,
  backGroundColor = ButtonColor.gray,
  textColor = TextColor.gray,
  icon,
  buttonSize = ButtonSizeList.base,
  iconClass = 'justify-between',
  ...otherProps
}) => {
  const IconComponent = icon?.component;
  const [isHoverState, setStateHover] = useState(false);

  return (
    <button
      className={cn(
        'flex items-center justify-center rounded-md',
        disabled && 'cursor-not-allowed',
        textColor.default,
        textColor.hover,
        textColor.active,
        textColor.disabled,
        backGroundColor.default,
        className,
        buttonSize
      )}
      onClick={onClick}
      onMouseEnter={() => setStateHover(true)}
      onMouseLeave={() => setStateHover(false)}
      disabled={disabled}
      {...otherProps}
    >
      <div className={cn('flex w-full h-full px-[16px] py-[8px]', iconClass)}>
        {icon && (
          <div
            className={cn(
              'flex items-center ',
              variant === ButtonVariant.regular && 'pr-[8px]'
            )}
          >
            <IconComponent
              {...(isHoverState ? ButtonIcon.hover : ButtonIcon.default)}
              {...(disabled ? ButtonIcon.disabled : {})}
              {...(icon?.size || {})}
            />
          </div>
        )}
        {variant === ButtonVariant.regular && (
          <div
            className={cn(
              'flex justify-center items-center w-full rounded-r-md fs-button-bold'
            )}
          >
            {buttonLabel}
          </div>
        )}
      </div>
    </button>
  );
};

export default Button;
